/**
 * Passenger utilities
 *
 * Components work with an internal details.fields format (built by
 * build_passengers_model).  The two booking APIs expect different shapes:
 *
 * ── FLIGHT_AND_STAY (alpitour) ─────────────────────────────────────
 *   { id, age, passengerType, details: { fields: [{ name, value }, ...] } }
 *
 * ── ONLY_STAY (sololand) ──────────────────────────────────────────
 *   { type_pax: 'MR', gender: 'M', name: 'Mario', surname: 'Rossi',
 *     birthday: '1980-07-19', nationality: 'ITA',
 *     prefix_phone: '0039', phone: '3333112123' }
 *
 * These functions convert at the booking boundary so each API receives
 * its expected shape while the frontend stays format-agnostic.
 */

import { field_map } from '@resources/passenger'

// Reverse map: canonical name → original API name
const reverse_field_map = Object.fromEntries(
  Object.entries(field_map).map(([api_name, canonical]) => [canonical, api_name])
)

/**
 * Convert an internal passenger (details.fields) to the flat format
 * expected by the sololand /book endpoint.
 *
 * @param {Object} pax - Internal passenger { id, age, passengerType, details: { fields: [...] } }
 * @returns {Object} Flat object e.g. { type_pax: 'MR', gender: 'M', name: 'Mario', ... }
 */
export const passenger_to_flat = (pax) => {
  const flat = {}
  const { details = {} } = pax || {}
  const { fields = [] } = details
  fields.forEach((field) => {
    if (!field.value && field.value !== 0 && field.value !== false) return
    flat[field.name] = field.value
  })
  return flat
}

/**
 * Convert an internal passenger (details.fields) to the fields format
 * expected by the alpitour /book endpoint.
 * Restores original API field names via reverse_field_map.
 *
 * @param {Object} pax - Internal passenger { id, age, passengerType, details: { fields: [...] } }
 * @returns {Object} { id, age, passengerType, details: { fields: [{ name, value }] } }
 */
export const passenger_to_fields = (pax) => {
  const { details = {} } = pax || {}
  const { fields = [] } = details
  return {
    id: pax.id,
    age: pax.age,
    passengerType: pax.passengerType,
    details: {
      fields: fields.map((field) => ({
        name: reverse_field_map[field.name] || field.name,
        value: field.value,
      })),
    },
  }
}

/**
 * Build passengers for ONLY_STAY (sololand) /book endpoint.
 *
 * @param {Array} passengers - Internal passengers array from store
 * @returns {Array} Array of flat passenger objects
 */
export const build_sololand_passengers = (passengers) =>
  (passengers || []).map(passenger_to_flat)

/**
 * Build passengers for FLIGHT_AND_STAY (alpitour) /book endpoint.
 *
 * @param {Array} passengers - Internal passengers array from store
 * @returns {Array} Array of { id, age, passengerType, details: { fields } } objects
 */
export const build_alpitour_passengers = (passengers) =>
  (passengers || []).map(passenger_to_fields)

/**
 * Extract specific fields from a passenger object regardless of format.
 * Looks first in flat properties (sololand / top-level), then in details.fields (alpitour).
 * For details.fields, tries both canonical and raw API names via field_map / reverse_field_map.
 * Only includes fields that are present and defined.
 *
 * @param {Object} passenger - Passenger in any format (flat or internal)
 * @param {string[]} fields - Field names to extract
 * @returns {Object} Object with only the found fields
 *
 * @example get_passenger_field(pax, ['age', 'nationality', 'missing']) => { age: 40, nationality: 'IT' }
 */
export const get_passenger_field = (passenger, fields) => {
  if (!passenger || !fields) return {}
  return fields.reduce((result, field) => {
    if (passenger[field] !== undefined && passenger[field] !== null) {
      result[field] = passenger[field]
      return result
    }
    if (passenger.details && passenger.details.fields) {
      const alt_name = reverse_field_map[field] || field_map[field]
      const found = passenger.details.fields.find(
        (f) => f.name === field || (alt_name && f.name === alt_name)
      )
      if (found && found.value !== undefined && found.value !== null) {
        result[field] = found.value
      }
    }
    return result
  }, {})
}
